import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAdvancedMatch from '../../hooks/queries/useAdvancedMatch';
import AdvancedComparisonControls from './AdvancedComparisonControls';
import TacticalSummary from './TacticalSummary';
import { advancedComparisonCandidates, advancedGroups, advancedKeyMetrics, advancedMetricLabel, advancedMetricValue,
  advancedNumber } from '../../utils/advancedStatistics';
import { playerName } from '../../utils/playerRanking';
import './advancedStatistics.css';

export default function AdvancedStatistics({ record, kind = 'player', id }) {
  const { t, i18n } = useTranslation('advanced');
  const titleId = useId();
  const [scope, setScope] = useState('total');
  const [period, setPeriod] = useState('partido');
  const [mode, setMode] = useState(kind === 'team' ? 'media' : 'suma');
  const [category, setCategory] = useState('key');
  const [comparing, setComparing] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [minimumMinutes, setMinimumMinutes] = useState(0);
  const { acta, isLoading, isError, refetch } = useAdvancedMatch(record);
  const advanced = record.estadisticasAvanzadas;
  const data = advanced?.[scope];
  const minimum = advancedNumber(minimumMinutes);
  const eligible = minimum !== null && minimum >= 0 && advancedNumber(data?.minutosConDatos) > 0
    && advancedNumber(data?.minutosConDatos) >= minimum;
  const comparison = comparing && eligible
    ? advancedComparisonCandidates(record, [selectedPlayer].filter(Boolean), minimumMinutes, scope)[0] : null;
  const groups = advancedGroups(data, kind, period, comparison?.estadisticasAvanzadas?.[scope]);
  const allMetrics = groups.flatMap(group => group.metrics);
  const active = groups.find(group => group.id === category);
  const metrics = active ? active.metrics : category === 'all' ? allMetrics : advancedKeyMetrics(allMetrics);
  const format = (value, decimals = 2) => value === null ? '—' : value.toLocaleString(i18n.language, {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
  const label = metric => t(`metrics.${metric}`, {
    defaultValue: advancedMetricLabel(metric, acta?.estadisticasJugadores?.catalogoMetricas, acta)
      || (metric.startsWith('FUENTE_') ? t('sourceMetric', { id: metric.slice(7) }) : metric.replaceAll('_', ' ')),
  });
  const sample = metric => metric && advancedNumber(metric.partidosConDatos) !== null
    ? t('matches', { count: Number(metric.partidosConDatos) }) : '—';
  const value = (metric, key) => {
    const number = advancedMetricValue(metric, mode, comparison ? minimum : 0);
    const percentage = metric?.operacion === 'porcentaje' || key === 'POSSESSION';
    const decimals = percentage || metric?.operacion === 'media' || mode !== 'suma'
      || key.startsWith('EXPECTED_') || key.startsWith('GOALS_PREVENTED')
      || key === 'DERIVED_GOALS_MINUS_XG' ? 2 : 0;
    return <><strong>{format(number, decimals)}{number !== null && percentage ? '%' : ''}</strong>
      {metric?.operacion === 'porcentaje' && advancedNumber(metric.aciertos) !== null
        && advancedNumber(metric.intentos) !== null && <small>{format(Number(metric.aciertos), 0)}
          {' / '}{format(Number(metric.intentos), 0)}</small>}</>;
  };
  const coverage = kind === 'player' ? data : data?.[period];
  const matches = advancedNumber(coverage?.partidosConDatos);
  return <section className="as-section" id={id} aria-labelledby={titleId}>
    <header className="as-heading">
      <div><h2 id={titleId}>{t('title')}</h2><p>{t(kind === 'team' ? 'teamIntro' : 'playerIntro')}</p></div>
      <div className="as-provenance">
        <strong>{matches === null ? t('coverageUnknown') : t('matches', { count: matches })}</strong>
        {kind === 'player' && advancedNumber(data?.minutosConDatos) !== null
          && <span>{t('minutes', { count: Number(data.minutosConDatos) })}</span>}</div>
    </header>
    {advancedNumber(data?.partidosCoberturaIndividualParcial) > 0 && <p className="as-note" role="note">
      {t('partialPlayerCoverage', { count: Number(data.partidosCoberturaIndividualParcial) })}
    </p>}
    <div className="as-controls">
      <label>{t('scope')}<select aria-label={t('scope')} value={scope} onChange={event => setScope(event.target.value)}>
        {['total', 'local', 'visitante'].map(key => <option key={key} value={key}>{t(`scopes.${key}`)}</option>)}
      </select></label>
      {kind === 'team' && <label>{t('period')}<select aria-label={t('period')} value={period} onChange={event => setPeriod(event.target.value)}>
        {['partido', 'primeraParte', 'segundaParte'].map(key => <option key={key} value={key}>{t(`periods.${key}`)}</option>)}
      </select></label>}
      <label>{t('measure')}<select aria-label={t('measure')} value={mode} onChange={event => setMode(event.target.value)}>
        {(kind === 'team' ? ['suma', 'media'] : ['suma', 'media', 'por90']).map(key =>
          <option key={key} value={key}>{t(`modes.${key}`)}</option>)}
      </select></label>
    </div>
    {kind === 'player' && record.idJugador != null && record.idGrupo != null
      && record.idCompeticion != null && record.idTemporada != null && advanced?.fuente && <>
      <button type="button" className="as-retry" aria-expanded={comparing}
        onClick={() => setComparing(value => !value)}>{t('comparison.title')}</button>
      {comparing && <AdvancedComparisonControls record={record} scope={scope} selected={selectedPlayer}
        onSelect={setSelectedPlayer} minimumMinutes={minimumMinutes} onMinimumChange={setMinimumMinutes}
        eligible={eligible} />}
    </>}
    <div className="as-categories" role="group" aria-label={t('category')}>
      <button type="button" aria-pressed={!active && category !== 'all'} onClick={() => setCategory('key')}>{t('key')}</button>
      {groups.map(group => <button key={group.id} type="button" aria-pressed={active?.id === group.id}
        onClick={() => setCategory(group.id)}>{t(`groups.${group.id}`, { defaultValue: group.id })}</button>)}
      <button type="button" aria-pressed={category === 'all'} onClick={() => setCategory('all')}>{t('all')}</button>
    </div>
    <div className={`as-body${kind === 'team' ? ' as-body-team' : ''}`}>
      <div className="as-metrics">
        {metrics.length ? <div className="as-table-scroll" tabIndex={0} role="region" aria-label={t('table')}>
          <table className="as-table" aria-label={t('table')}>
            <thead><tr><th scope="col">{t('metric')}</th>
              <th scope="col">{comparison ? playerName(record) : t(kind === 'team' ? 'for' : 'value')}</th>
              <th scope="col">{comparison ? playerName(comparison) : t(kind === 'team' ? 'against' : 'sample')}</th></tr></thead>
            <tbody>{metrics.map(metric => <tr key={metric.id}>
              <th scope="row">{label(metric.id)}
                {(metric.first?.operacion === 'media' || metric.second?.operacion === 'media')
                  && <small>{t('alwaysAverage')}</small>}
                {(metric.first?.derivada || metric.second?.derivada) && <small>{t(`formulas.${metric.id}`)}</small>}</th>
              <td>{value(metric.first, metric.id)}{(kind === 'team' || comparison) && <small>{sample(metric.first)}</small>}
                {comparison && advancedNumber(metric.first?.minutosConDatos) !== null
                  && <small>{t('minutes', { count: Number(metric.first.minutosConDatos) })}</small>}</td>
              <td>{kind === 'team' ? <>{value(metric.second, metric.id)}<small>{sample(metric.second)}</small></>
                : comparison ? <>{value(metric.second, metric.id)}<small>{sample(metric.second)}</small>
                  {advancedNumber(metric.second?.minutosConDatos) !== null
                    && <small>{t('minutes', { count: Number(metric.second.minutosConDatos) })}</small>}</>
                : <><span>{sample(metric.first)}</span>{advancedNumber(metric.first?.minutosConDatos) !== null
                  && <small>{t('minutes', { count: Number(metric.first.minutosConDatos) })}</small>}</>}</td>
            </tr>)}</tbody>
          </table>
        </div> : <p className="as-empty">{t('empty')}</p>}
        <p className="as-note">{t('method')}</p>
        {kind === 'player' && <p className="as-note">{t('derivedMethod')}</p>}
      </div>
      {kind === 'team' && <TacticalSummary key={`${record.id}:${scope}`} record={record}
        tactics={data?.tactica} acta={acta} t={t} format={format} />}
    </div>
    {isLoading && <p className="as-note" role="status">{t('loadingContext')}</p>}
    {isError && <p className="as-note" role="status">{t('contextError')} <button type="button" className="as-retry"
      onClick={() => refetch()}>{t('retry')}</button></p>}
  </section>;
}

export function PlayerAdvancedCareer({ records, season, seasons = [] }) {
  const { t } = useTranslation('advanced');
  const [selected, setSelected] = useState('');
  const candidates = records.filter(record => record.estadisticasAvanzadas
    && (!season || String(record.idTemporada) === String(season)))
    .sort((a, b) => Number(b.idTemporada) - Number(a.idTemporada));
  const record = candidates.find(row => row.id === selected) || candidates[0];
  if (!record) return null;
  return <div className="as-career">
    <label className="as-stage">{t('stage')}<select aria-label={t('stage')} value={record.id} onChange={event => setSelected(event.target.value)}>
      {candidates.map(row => <option key={row.id} value={row.id}>
        {seasons.find(item => String(item.id) === String(row.idTemporada))?.nombre || row.idTemporada}
        {' · '}{row.equipo || row.nombreEquipo}{row.competicion?.nombre ? ` · ${row.competicion.nombre}` : ''}
      </option>)}
    </select></label>
    <AdvancedStatistics key={record.id} record={record} />
  </div>;
}
